<template>
  <div>
    <div class="col-12 px-0">
      <h1 class="d-flex align-items-center">
        <span class="icon mr-2 icon-kalender" aria-hidden="true"></span>
        {{ $t('views.timeSlotAdd.title') }}
      </h1>
    </div>

    <link-back :to="{ name: 'admin-time-slots', params: { id: roomId } }" i18nMessage="common.backToOverview" class="mt-2" />

    <h2 class="my-2">{{ room.name }}</h2>

    <loading-container :state="state" :i18nErrorMessage="getI18nErrorString()">
      <form @submit.prevent="onSubmit()">

        <div class="row mx-auto">
          <div class="col-12 col-md-4 d-flex bg-lightblue p-3 kapazitaetrow">

            <control-time
              i18nLabel="common.timeSlot.startTime"
              :validation="v$.startTime"
              v-model="startTime"
              :showBreak="true"
              class=" d-flex flex-wrap align-items-center mr-3"
            />

          </div>
          <div class="col-12 col-md-4 d-flex bg-lightblue p-3 kapazitaetrow">

            <control-time
              i18nLabel="common.timeSlot.endTime"
              :validation="v$.endTime"
              v-model="endTime"
              :showBreak="true"
              class=" d-flex flex-wrap align-items-center mr-3"
            />

          </div>
          <div class="col-12 col-md-4 d-flex bg-lightblue p-3 kapazitaetrow">

            <control-integer
              i18nLabel="common.timeSlot.capacity"
              :validation="v$.capacity"
              v-model="capacity"
              :showBreak="true"
              class=" d-flex flex-wrap align-items-center mr-3"
            />

          </div>
        </div>

        <div class="row mx-auto mt-3">
          <div class="col-12 col-md-6 pl-md-0">
            <control-date
              i18nLabel="common.timeSlot.startDate"
              :validation="v$.startDate"
              v-model="startDate"
              @change="setEndDate"
            />
          </div>
          <div class="col-12 col-md-6 pr-md-0">
            <control-date
              i18nLabel="common.timeSlot.repeatUntil"
              :validation="v$.repeatUntil"
              v-model="repeatUntil"
              @change="showRepeaterFunc"
            />
          </div>
        </div>

        <div v-if="showRepeater">
          <control-week-days
            i18nLabel="common.timeSlot.daysOfWeek"
            :validation="v$.daysOfWeek"
            v-model="daysOfWeek"
          >
          <control-checkbox
            i18nLabel="common.timeSlot.includeHolidays"
            :validation="v$.includeHolidays"
            v-model="includeHolidays"
            class="mt-3 mb-0"
          />
          </control-week-days>
        </div>


        <div class="row mx-auto mt-4">
          <div class="col-12 col-md-4 offset-md-2 col-lg-3 offset-lg-3 mt-1 px-xs-0">
            <button-cancel :to="{ name: 'admin-time-slots', PARAMS: { id: roomId }}" class="btn-w-100" />
          </div>
          <div class="col-12 col-md-4 col-lg-3 mt-1 px-xs-0">
            <button-save i18nMessage="common.timeSlot.add" class="btn-w-100" />
          </div>
        </div>

      </form>
    </loading-container>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import LoadingMixin from '@/mixins/LoadingMixin'

import { CreateTimeSlotDefinition, CreateTimeSlotDefinitionDaysOfWeekEnum, Room } from '@/generated/openapi';
import { adminService } from '@/services/adminService';
import { smartBack, urlParamString } from '@/utils/navigationUtils';
import { LoadingState } from '@/utils/loadingUtils';
import { validateAndFocusInvalid } from '@/utils/formUtils';
import { requiredTime, isDateString, requiredInteger, requiredWeekDay, isAfterStartTime, isAfterStartDate } from '@/utils/validationUtils';
import useVuelidate from '@vuelidate/core';

import LoadingContainer from '@/components/structure/LoadingContainer.vue'
import ControlInteger from '@/components/controls/ControlInteger.vue'
import ControlDate from '@/components/controls/ControlDate.vue'
import ControlTime from '@/components/controls/ControlTime.vue'
import ControlWeekDays from '@/components/controls/ControlWeekDays.vue'
import ButtonSave from '@/components/interaction/ButtonSave.vue'
import ButtonCancel from '@/components/interaction/ButtonCancel.vue'
import { between, required } from '@vuelidate/validators';
import { now } from '@/utils/dateTimeUtils';
import LinkBack from '@/components/interaction/LinkBack.vue'
import ControlCheckbox from '@/components/controls/ControlCheckbox.vue';
import { InfoPopupBase, InfoPopupType } from '@/models/InfoPopupData';

export default defineComponent({
  name: 'TimeSlotAdd',
  mixins: [
    LoadingMixin
  ],
  components: {
    LoadingContainer,
    ControlInteger,
    ControlDate,
    ControlTime,
    ControlWeekDays,
    ButtonCancel,
    ButtonSave,
    LinkBack,
    ControlCheckbox
  },
  setup() {
    return {
      v$: useVuelidate()
    }
  },
  data() {
    return {
      roomId: '',
      room: {} as Room,
      startTime: '00:00',
      endTime: '23:59',
      startDate: now().toISODate(),
      repeatUntil: now().toISODate(),
      showRepeater: false,
      daysOfWeek: new Set([
        CreateTimeSlotDefinitionDaysOfWeekEnum.Monday,
        CreateTimeSlotDefinitionDaysOfWeekEnum.Tuesday,
        CreateTimeSlotDefinitionDaysOfWeekEnum.Wednesday,
        CreateTimeSlotDefinitionDaysOfWeekEnum.Thursday,
        CreateTimeSlotDefinitionDaysOfWeekEnum.Friday
      ]) as Set<CreateTimeSlotDefinitionDaysOfWeekEnum>,
      includeHolidays: false,
      capacity: 0,
    }
  },
  validations() {
    return {
      startTime: {
        requiredTime
      },
      endTime: {
        requiredTime,
        isAfterStartTime
      },
      startDate: {
        required,
        isDateString
      },
      repeatUntil: {
        isAfterStartDate
      },
      daysOfWeek: {
        requiredWeekDay
      },
      includeHolidays: {

      },
      capacity: {
        requiredInteger,
        between: between(0, 99999)
      }
    }
  },
  created() {
    this.roomId = urlParamString(this.$route, 'id')
    adminService.getRoom(this.roomId).then(room => {
      this.room = room
      this.capacity = room.capacity || 0
      this.state = LoadingState.success
    }).catch(this.handleError)
  },
  methods: {
    asCreateTimeSlot(): CreateTimeSlotDefinition {
      return {
        startTime: this.startTime,
        endTime: this.endTime,
        startDate: this.startDate,
        repeatUntil: this.repeatUntil === this.startTime ? undefined : this.repeatUntil,
        daysOfWeek: [...this.daysOfWeek],
        includeHolidays: this.includeHolidays,
        capacity: this.capacity
      }
    },
    onSubmit() {
      validateAndFocusInvalid(this.v$, this.$el).then(this.createTimeslot)
    },
    createTimeslot() {
      this.state = LoadingState.loading;

      if(this.repeatUntil === this.startDate){
          this.includeHolidays = true
          this.daysOfWeek = new Set([
          CreateTimeSlotDefinitionDaysOfWeekEnum.Monday,
          CreateTimeSlotDefinitionDaysOfWeekEnum.Tuesday,
          CreateTimeSlotDefinitionDaysOfWeekEnum.Wednesday,
          CreateTimeSlotDefinitionDaysOfWeekEnum.Thursday,
          CreateTimeSlotDefinitionDaysOfWeekEnum.Friday,
          CreateTimeSlotDefinitionDaysOfWeekEnum.Saturday,
          CreateTimeSlotDefinitionDaysOfWeekEnum.Sunday
          ]) as Set<CreateTimeSlotDefinitionDaysOfWeekEnum>
        }

      adminService.createTimeSlot(this.room.id, this.asCreateTimeSlot()).then((resp) => {
        const count = resp.createdTimeSlots
        this.$store.commit('addInfoPopup', {
          title: this.$t('views.timeSlotAdd.confirmation.title', { count }),
          type: InfoPopupType.info
        } as InfoPopupBase)
          smartBack(this.$router, {
            path: '/reservations'
          })
      }).catch(this.handleErrorWithI18nString)
    },
    setEndDate(){
      this.repeatUntil = this.startDate
    },
    showRepeaterFunc(){
      this.showRepeater = false
      if(this.repeatUntil >= this.startDate){
        this.showRepeater = true
      }
    }
  }
});
</script>
